import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Modal from '../../../../shared/_TBComponents/Modal';
import Icon from '../../../../shared/_TBComponents/Icon';

const Body = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 24px 0;
  font-family: 'Nunito Sans';
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #D6D6D6;
`;

const Header = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 16px;
`;

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled('h2')`
  margin: 0;
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
`;

const CloseButton = styled('button')`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const Subtitle = styled('p')`
  margin: 0;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
`;

const Divider = styled('div')`
  height: 1px;
  background: #D6D6D6;
  width: 100%;
`;

const Section = styled('div')`
  padding: 8px 24px;
`;

const HelperRow = styled('p')`
  margin: 0;
  padding: 0 24px;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
`;

const TableRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #D6D6D6;
  padding: ${(p) => (p.header ? '4px 8px' : '8px')};
  ${(p) => (p.header
    ? 'border-radius: 4px 4px 0 0;'
    : 'border-top: none; &:last-of-type { border-radius: 0 0 4px 4px; }')}
`;

/** Clamp keeps the list from pushing the create action below the fold on
 * short screens (98px floor ~2 rows); the header row stays fixed above it. */
const ScrollableRows = styled('div')`
  max-height: clamp(98px, calc(100vh - 520px), 245px);
  overflow-y: auto;
`;

const NameCell = styled('span')`
  flex: 1;
  min-width: 0;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: ${(p) => (p.bold ? 700 : 400)};
  line-height: 1.5;
  color: #1A1A1A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MetaCell = styled('span')`
  width: 160px;
  flex-shrink: 0;
  text-align: center;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: ${(p) => (p.bold ? 700 : 400)};
  line-height: 1.5;
  color: #1A1A1A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 991px) { width: 110px; }
`;

const UseThisButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.39px;
  padding: 6px 16px;
  border: none;
  background: none;
  color: #1787FB;
  cursor: pointer;
  white-space: nowrap;

  :hover { opacity: 0.7; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

/* Invisible copy of the widest action control, so header text columns line up
   with the row cells (the design reserves the action column the same way). */
const ActionSpacer = styled('span')`
  visibility: hidden;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.39px;
  padding: 6px 16px;
  white-space: nowrap;
  min-width: ${(p) => p.minWidth || 'auto'};
  text-align: center;
`;

const NameEditGroup = styled('span')`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const NameViewBox = styled('span')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  min-width: 0;
`;

const PencilButton = styled('button')`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const NameInput = styled('input')`
  flex: 1;
  min-width: 0;
  border: 2px solid #1787FB;
  border-radius: 4px;
  padding: 4px;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
  outline: none;
`;

const CreateButton = styled('button')`
  width: 84px;
  padding: 6px 16px;
  border: none;
  border-radius: 4px;
  background: ${(p) => (p.disabled ? '#E0E0E0' : '#1A1A1A')};
  color: #FFFFFF;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.39px;
  text-align: center;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  white-space: nowrap;

  &:hover { opacity: ${(p) => (p.disabled ? 1 : 0.85)}; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

/* The icomoon set has no pencil-in-square glyph matching the design, so the
   design's icon is inlined. */
const EditPencilIcon = () => (
  <svg width='19' height='19' viewBox='0 0 19.1213 19.1213' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
    <path fillRule='evenodd' clipRule='evenodd' d='M18.2071 0.914214C16.9882 -0.304738 15.0118 -0.304738 13.7929 0.914214L12.2932 2.41394C12.2931 2.41403 12.293 2.41412 12.2929 2.41421C12.2928 2.41431 12.2927 2.4144 12.2926 2.41449L5.29289 9.41421L5.20608 9.51329C5.07316 9.68684 5 9.90031 5 10.1213V13.1213L5.00673 13.2379C5.06449 13.7353 5.48716 14.1213 6 14.1213H9L9.13144 14.1126C9.34814 14.0839 9.55083 13.9847 9.70711 13.8284L16.7071 6.82843L18.2071 5.32843L18.3423 5.1845C19.4243 3.95857 19.3792 2.08628 18.2071 0.914214ZM15.9999 4.70702L16.7929 3.91421C17.1995 3.50759 17.2286 2.86637 16.88 2.42623L16.7929 2.32843C16.3863 1.9218 15.745 1.89276 15.3049 2.24129L15.2071 2.32843L14.4142 3.12131L15.9999 4.70702ZM13 4.5355L14.5855 6.12108L8.585 12.1203H7V10.5353L13 4.5355ZM6 4.12132C6.55228 4.12132 7 4.56904 7 5.12132C7 5.63416 6.61396 6.05683 6.11662 6.11459L6 6.12132H3C2.48716 6.12132 2.06449 6.50736 2.00673 7.0047L2 7.12132V16.1213C2 16.6342 2.38604 17.0568 2.88338 17.1146L3 17.1213H12C12.5128 17.1213 12.9355 16.7353 12.9933 16.2379L13 16.1213V13.1213C13 12.569 13.4477 12.1213 14 12.1213C14.5128 12.1213 14.9355 12.5074 14.9933 13.0047L15 13.1213V16.1213C15 17.719 13.7511 19.025 12.1763 19.1162L12 19.1213H3C1.40232 19.1213 0.0963391 17.8724 0.00509269 16.2976L0 16.1213V7.12132C0 5.52364 1.24892 4.21766 2.82373 4.12641L3 4.12132H6Z' fill='#1787FB' />
  </svg>
);

/** Title-case the API's plain uppercase typeDescriptions (LIFT -> Lift);
 symbol-bearing values like SAQ+C render as-is. */
const formatType = (typeDescription) => {
  if (!typeDescription) return '-';
  if (!/^[A-Z_]+$/.test(typeDescription)) return typeDescription;
  return typeDescription.charAt(0) + typeDescription.slice(1).toLowerCase();
};

/** Lets the coach adopt an existing matching exercise instead of creating a
 * duplicate, or proceed anyway; closing backs out to the still-open create form. */
const SimilarExercisesCheckModal = ({
  isOpen,
  proposedExercise,
  similarExercises,
  onUseExisting,
  onProceed,
  onClose,
}) => {
  const [nameValue, setNameValue] = useState(proposedExercise?.name || '');
  const [isEditingName, setIsEditingName] = useState(false);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      hideButton
      hideTitle
      customMaxWidth='808px'
      customMinHeight='0'
      customPadding='0'
      customBorderRadius='8px'
    >
      <Body>
        <Header>
          <TitleRow>
            <Title>Check Similar Exercises</Title>
            <CloseButton type='button' onClick={onClose} aria-label='Close'>
              <Icon icon='remove' size={16} color='#929292' />
            </CloseButton>
          </TitleRow>
          <Subtitle>
            There are existing exercises that are similar to the one you are creating.
          </Subtitle>
        </Header>

        <Divider />

        <Section>
          <TableRow header>
            <NameCell bold>Similar Exercises</NameCell>
            <MetaCell bold>Type</MetaCell>
            <MetaCell bold>Tracking</MetaCell>
            <ActionSpacer>Use this</ActionSpacer>
          </TableRow>
          <ScrollableRows>
            {similarExercises.map((ex) => (
              <TableRow key={ex.id}>
                <NameCell title={ex.name}>{ex.name}</NameCell>
                <MetaCell>{formatType(ex.typeDescription)}</MetaCell>
                <MetaCell>{ex.trackingTypeDescription || '-'}</MetaCell>
                <UseThisButton type='button' onClick={() => onUseExisting(ex)}>
                  Use this
                </UseThisButton>
              </TableRow>
            ))}
          </ScrollableRows>
        </Section>

        <HelperRow>
          If you would like to continue to create a new exercise, we recommend adjusting the name.
        </HelperRow>

        <Section>
          <TableRow header>
            <NameCell bold>Your new exercise</NameCell>
            <MetaCell bold>Type</MetaCell>
            <MetaCell bold>Tracking</MetaCell>
            <ActionSpacer minWidth='84px'>Create</ActionSpacer>
          </TableRow>
          <TableRow>
            <NameEditGroup>
              {isEditingName ? (
                <NameInput
                  autoFocus
                  name='similarCheckRename'
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onBlur={() => setIsEditingName(false)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setIsEditingName(false); }}
                />
              ) : (
                <NameViewBox>
                  <NameCell title={nameValue}>{nameValue}</NameCell>
                  <PencilButton
                    type='button'
                    aria-label='Edit exercise name'
                    onClick={() => setIsEditingName(true)}
                  >
                    <EditPencilIcon />
                  </PencilButton>
                </NameViewBox>
              )}
            </NameEditGroup>
            <MetaCell>{proposedExercise?.typeName || '-'}</MetaCell>
            <MetaCell>{proposedExercise?.trackingName || '-'}</MetaCell>
            <CreateButton
              type='button'
              disabled={!nameValue.trim()}
              onClick={() => onProceed(nameValue.trim())}
            >
              Create
            </CreateButton>
          </TableRow>
        </Section>
      </Body>
    </Modal>
  );
};

SimilarExercisesCheckModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  proposedExercise: PropTypes.shape({
    name: PropTypes.string,
    typeName: PropTypes.string,
    trackingName: PropTypes.string,
  }),
  similarExercises: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    typeDescription: PropTypes.string,
    trackingTypeDescription: PropTypes.string,
  })).isRequired,
  onUseExisting: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SimilarExercisesCheckModal.defaultProps = {
  proposedExercise: null,
};

export default SimilarExercisesCheckModal;
